import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Download,
  DollarSign,
  CreditCard,
  AlertCircle,
  Users,
  Wrench,
} from 'lucide-react';
import { paymentService } from '../../services/paymentService.js';
import userService from '../../services/UserService.js';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
} from '../common/index.js';

export default function DailyEarnings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [filterValues, setFilterValues] = useState({
    userFilter: '',
    fromDate: getTodayDate(),
    toDate: getTodayDate(),
  });

  const [statistics, setStatistics] = useState({
    totalEarnings: 0,
    paymentModeBreakdown: {},
    lateCharges: { count: 0, amount: 0 },
    labourCharges: { count: 0, amount: 0 },
    deliveryCharges: { count: 0, amount: 0 },
  });

  const perPage = 10;
  const isAdminRole =
    user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant';
  const isAgent = user?.role === 'Agent';

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    console.log('Agents state updated:', agents);
  }, [agents]);

  useEffect(() => {
    if (isAgent && agents.length > 0 && !filterValues.userFilter) {
      const currentAgent = agents.find((a) => a.user_id === user.user_id);
      if (currentAgent) {
        setFilterValues((prev) => ({ ...prev, userFilter: currentAgent.user_id }));
      }
    }
  }, [isAgent, agents, user]);

  useEffect(() => {
    if (filterValues.userFilter || isAdminRole) {
      setCurrentPage(1);
      fetchEarnings();
    }
  }, [filterValues.userFilter, filterValues.fromDate, filterValues.toDate]);

  const fetchAgents = async () => {
    try {
      const response = await userService.getAll(1, 1000, { role_id: 3, is_active: true });

      console.log('Agents API Response:', response);

      const usersArray = response.data || [];

      console.log('Users array:', usersArray);

      if (isAgent) {
        const currentAgent = usersArray.find((u) => u.user_id === user.user_id);
        if (currentAgent) {
          setAgents([currentAgent]);
          setFilterValues((prev) => ({ ...prev, userFilter: currentAgent.user_id }));
        } else {
          setAgents([]);
        }
      } else {
        setAgents(usersArray);
      }

      console.log('Agents state set to:', usersArray);
    } catch (error) {
      console.error('Error fetching agents:', error);
      if (!isAgent) {
        Alert.error('Failed to fetch agents');
      }
    }
  };

  const calculateStatistics = (paymentsData) => {
    const stats = {
      totalEarnings: 0,
      paymentModeBreakdown: {
        CASH: { count: 0, amount: 0 },
        BANK_TRANSFER: { count: 0, amount: 0 },
        UPI: { count: 0, amount: 0 },
        CHEQUE: { count: 0, amount: 0 },
      },
      lateCharges: { count: 0, amount: 0 },
      labourCharges: { count: 0, amount: 0 },
      deliveryCharges: { count: 0, amount: 0 },
    };

    paymentsData.forEach((payment) => {
      const amount = payment.payment_amount || 0;

      stats.totalEarnings += amount;

      const mode = (payment.payment_mode || '').toUpperCase().trim();

      if (mode === 'CASH') {
        stats.paymentModeBreakdown.CASH.count += 1;
        stats.paymentModeBreakdown.CASH.amount += amount;
      } else if (mode === 'BANK_TRANSFER' || mode === 'BANK TRANSFER') {
        stats.paymentModeBreakdown.BANK_TRANSFER.count += 1;
        stats.paymentModeBreakdown.BANK_TRANSFER.amount += amount;
      } else if (mode === 'UPI') {
        stats.paymentModeBreakdown.UPI.count += 1;
        stats.paymentModeBreakdown.UPI.amount += amount;
      } else if (mode === 'CHEQUE') {
        stats.paymentModeBreakdown.CHEQUE.count += 1;
        stats.paymentModeBreakdown.CHEQUE.amount += amount;
      }

      const remarks = (payment.remarks || '').toLowerCase();

      if (remarks.includes('late charge')) {
        stats.lateCharges.count += 1;
        const match = remarks.match(/₹\s*([0-9,]+\.?\d*)/);
        if (match) {
          const chargeAmount = parseFloat(match[1].replace(/,/g, ''));
          stats.lateCharges.amount += chargeAmount;
        }
      }

      if (remarks.includes('labour charge')) {
        stats.labourCharges.count += 1;
        const match = remarks.match(/₹\s*([0-9,]+\.?\d*)/);
        if (match) {
          const chargeAmount = parseFloat(match[1].replace(/,/g, ''));
          stats.labourCharges.amount += chargeAmount;
        }
      }

      if (
        remarks.includes('delivery charge') ||
        remarks.includes('handling charge') ||
        remarks.includes('hub delivery')
      ) {
        stats.deliveryCharges.count += 1;
        const match = remarks.match(/₹\s*([0-9,]+\.?\d*)/);
        if (match) {
          const chargeAmount = parseFloat(match[1].replace(/,/g, ''));
          stats.deliveryCharges.amount += chargeAmount;
        }
      }
    });

    console.log('Payment Statistics:', stats);
    setStatistics(stats);
  };

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const params = {
        all: 'true',
        from_date: filterValues.fromDate,
        to_date: filterValues.toDate,
        ...(filterValues.userFilter && { user_id: filterValues.userFilter }),
      };

      const response = await paymentService.getAllPaymentHistory(params);
      const paymentsData = response.data.data || [];
      setEarnings(paymentsData);
      calculateStatistics(paymentsData);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      Alert.error('Failed to fetch earnings data');
      setEarnings([]);
      setStatistics({
        totalEarnings: 0,
        paymentModeBreakdown: {},
        lateCharges: { count: 0, amount: 0 },
        labourCharges: { count: 0, amount: 0 },
        deliveryCharges: { count: 0, amount: 0 },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);

      const params = new URLSearchParams({
        from_date: filterValues.fromDate,
        to_date: filterValues.toDate,
        ...(filterValues.userFilter && { user_id: filterValues.userFilter }),
      });

      const response = await paymentService.exportDailyEarningsExcel(params.toString());

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const filename = `daily_earnings_${filterValues.fromDate}_to_${filterValues.toDate}_${new Date().getTime()}.xlsx`;
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      Alert.success('Daily earnings report exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      Alert.error('Failed to export daily earnings report');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    const today = getTodayDate();
    setFilterValues({
      userFilter: isAgent ? filterValues.userFilter : '',
      fromDate: today,
      toDate: today,
    });
  };

  const getPaymentModeDisplay = (mode) => {
    const displayNames = {
      CASH: 'Cash',
      BANK_TRANSFER: 'Bank Transfer',
      UPI: 'UPI',
      CHEQUE: 'Cheque',
    };
    return displayNames[mode] || mode.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const isFiltersActive = () => {
    const today = getTodayDate();
    return (!isAgent && filterValues.userFilter) || filterValues.fromDate !== today || filterValues.toDate !== today;
  };

  const totalPages = Math.max(1, Math.ceil(earnings.length / perPage));
  const paginatedEarnings = earnings.slice((currentPage - 1) * perPage, currentPage * perPage);

  const emptyMessage =
    !filterValues.userFilter && !isAgent
      ? 'Please select an agent to view earnings'
      : 'No payments recorded for the selected period';

  const filterConfig = [
    {
      key: 'userFilter',
      label: isAgent ? 'Select Agent (Your Account)' : 'Select Agent',
      type: 'select',
      options: agents.map((agent) => ({
        value: agent.user_id,
        label: `${agent.name || agent.username || 'Unknown'} (${agent.city})`,
      })),
    },
    { key: 'fromDate', label: 'From Date', type: 'date' },
    { key: 'toDate', label: 'To Date', type: 'date' },
  ];

  const columns = [
    {
      key: 'payment_date',
      label: 'Date & Time',
      render: (payment) =>
        payment.payment_date
          ? new Date(payment.payment_date).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : 'N/A',
    },
    {
      key: 'shipment_id',
      label: 'Shipment ID',
      render: (payment) => (
        <button
          type="button"
          onClick={() => navigate(`/shipments/view/${payment.shipment_id}`)}
          className="font-mono font-bold text-brand hover:underline text-sm"
        >
          {payment.shipment_id}
        </button>
      ),
    },
    {
      key: 'payment_amount',
      label: 'Amount',
      render: (payment) => (
        <span className="font-bold text-green-600">
          ₹{payment.payment_amount?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      key: 'payment_mode',
      label: 'Payment Mode',
      render: (payment) => (
        <span className="px-2 py-1 bg-blue-100 text-brand rounded text-xs font-semibold uppercase">
          {payment.payment_mode?.replace(/_/g, ' ') || 'N/A'}
        </span>
      ),
    },
    {
      key: 'previous_balance',
      label: 'Previous Balance',
      render: (payment) => `₹${payment.previous_balance?.toFixed(2) || '0.00'}`,
    },
    {
      key: 'new_balance',
      label: 'New Balance',
      render: (payment) => (
        <span className="font-bold text-red-600">
          ₹{payment.new_balance?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      key: 'recorded_by_name',
      label: 'Recorded By',
      render: (payment) => payment.recorded_by_name || 'N/A',
    },
    {
      key: 'remarks',
      label: 'Remarks',
      cellClassName: 'max-w-xs truncate',
      render: (payment) => payment.remarks || 'N/A',
    },
  ];

  return (
    <PageLayout
      title="Daily Earnings Report"
      icon={TrendingUp}
      maxWidth="max-w-full"
      actions={
        <Button
          icon={Download}
          onClick={handleExportExcel}
          disabled={exporting || earnings.length === 0}
          loading={exporting}
        >
          Export Excel Report
        </Button>
      }
    >
      <p className="text-sm text-gray-600 mb-4 -mt-2">View and analyze payment collections</p>

      <FilterPanel
        filters={filterConfig}
        values={filterValues}
        onChange={setFilterValues}
        onClear={isFiltersActive() ? clearFilters : undefined}
        title="Report Filters"
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600 font-semibold">Loading earnings data...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-300 p-4 rounded-card shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-7 h-7 text-brand" />
              </div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-secondary mt-1">
                ₹{statistics.totalEarnings.toFixed(2)}
              </p>
            </div>

            <div className="bg-white border border-gray-300 p-4 rounded-card shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
              <p className="text-sm font-medium text-gray-600">Late Charges</p>
              <p className="text-2xl font-bold text-secondary mt-1">
                ₹{statistics.lateCharges.amount.toFixed(2)}
              </p>
            </div>

            <div className="bg-white border border-gray-300 p-4 rounded-card shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-7 h-7 text-brand" />
              </div>
              <p className="text-sm font-medium text-gray-600">Labour Charges</p>
              <p className="text-2xl font-bold text-secondary mt-1">
                ₹{statistics.labourCharges.amount.toFixed(2)}
              </p>
            </div>

            <div className="bg-white border border-gray-300 p-4 rounded-card shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-2">
                <Wrench className="w-7 h-7 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-600">Delivery Charges</p>
              <p className="text-2xl font-bold text-secondary mt-1">
                ₹{statistics.deliveryCharges.amount.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-secondary mb-3">Payment Mode Breakdown</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE'].map((mode) => {
                const data = statistics.paymentModeBreakdown[mode] || { count: 0, amount: 0 };

                return (
                  <div
                    key={mode}
                    className="bg-white border border-gray-300 p-4 rounded-card shadow-sm hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <CreditCard className="w-7 h-7 text-brand" />
                      <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {data.count} txn
                      </span>
                    </div>
                    <h4 className="text-sm font-medium text-gray-600 mb-1">
                      {getPaymentModeDisplay(mode)}
                    </h4>
                    <p className="text-2xl font-bold text-secondary">₹{data.amount.toFixed(2)}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <h3 className="text-lg font-semibold text-secondary mb-3">Payment Transactions</h3>

          <DataTable
            columns={columns}
            data={paginatedEarnings}
            loading={false}
            emptyMessage={emptyMessage}
            rowKey="id"
          />

          {earnings.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </PageLayout>
  );
}
